'use client'

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

/** The signed-in person, as Firebase gives them. */
type SignedIn = { uid: string; getIdToken: () => Promise<string> }

/** How long the page's language is left to settle before it is sent: it is detected just after the page loads. */
const SETTLE_MS = 1500

/**
 * Tells v2 the signed-in person's language (src/lib/api/language-v2.ts), in the background: after sign-in or a page
 * load, and whenever they switch language. Nothing waits for it, and it is loaded only once someone is signed in.
 */
export function useKeepLanguage(user: SignedIn | null): void {
  const { i18n } = useTranslation()
  const [language, setLanguage] = useState<string>(i18n.language)

  useEffect(() => {
    const changed = (next: string) => setLanguage(next)
    i18n.on('languageChanged', changed)
    return () => i18n.off('languageChanged', changed)
  }, [i18n])

  useEffect(() => {
    if (!user || !language) return
    const timer = setTimeout(() => {
      import('../lib/api/language-v2.ts')
        .then(({ keepLanguage }) =>
          keepLanguage(user.uid, () => user.getIdToken(), i18n.language),
        )
        .catch(() => undefined)
    }, SETTLE_MS)
    return () => clearTimeout(timer)
  }, [user, language, i18n])
}
